/**
 * Developer Logging Panel
 * 
 * A collapsible side panel that shows:
 * - Real-time API call logs
 * - Defender write-back operation logs
 * - Error traces with context
 * 
 * Always visible in this dev-only environment.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '../../lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export type LogType = 'api' | 'defender' | 'error';
export type LogStatus = 'pending' | 'success' | 'error';

export interface LogEntry {
  id: string;
  type: LogType;
  status: LogStatus;
  timestamp: Date;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  endpoint: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response?: any;
  error?: string;
  duration?: number;
}

// ============================================================================
// LOG STORE (Singleton)
// ============================================================================

type LogSubscriber = (logs: LogEntry[]) => void;

class DevLogStore {
  private logs: LogEntry[] = [];
  private subscribers: Set<LogSubscriber> = new Set();
  private maxLogs = 100;
  // Always enabled - this is a dev-only environment
  private enabled = true;

  subscribe(callback: LogSubscriber): () => void {
    this.subscribers.add(callback);
    callback(this.logs);
    return () => this.subscribers.delete(callback);
  }

  private notify(): void {
    this.subscribers.forEach(cb => cb([...this.logs]));
  }

  addLog(entry: Omit<LogEntry, 'id' | 'timestamp'>): string {
    if (!this.enabled) return '';
    
    const id = `log-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const newEntry: LogEntry = {
      ...entry,
      id,
      timestamp: new Date(),
    };
    
    this.logs = [newEntry, ...this.logs].slice(0, this.maxLogs);
    this.notify();
    return id;
  }

  updateLog(id: string, updates: Partial<LogEntry>): void {
    if (!this.enabled) return;
    
    this.logs = this.logs.map(log => 
      log.id === id ? { ...log, ...updates } : log
    );
    this.notify();
  }

  clearLogs(): void {
    this.logs = [];
    this.notify();
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
}

export const devLogStore = new DevLogStore();

// ============================================================================
// API INTERCEPTOR HOOK
// ============================================================================

/**
 * Log an API call. Returns functions to update the log with success/error.
 */
export function logApiCall(
  method: LogEntry['method'],
  endpoint: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any
): {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  success: (response: any, duration: number) => void;
  error: (error: string, duration: number) => void;
} {
  const type: LogType = endpoint.includes('/defender/') ? 'defender' : 'api';
  
  const id = devLogStore.addLog({
    type,
    status: 'pending',
    method,
    endpoint,
    payload,
  });

  return {
    success: (response: unknown, duration: number) => {
      devLogStore.updateLog(id, {
        status: 'success',
        response,
        duration,
      });
    },
    error: (error: string, duration: number) => {
      devLogStore.updateLog(id, {
        status: 'error',
        error,
        duration,
      });
    },
  };
}

// ============================================================================
// DEV PANEL COMPONENT
// ============================================================================

interface DevPanelProps {
  className?: string;
}

export function DevPanel({ className }: DevPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<LogType | 'all'>('all');
  const [enabled, setEnabled] = useState(devLogStore.isEnabled());
  const panelRef = useRef<HTMLDivElement>(null);

  // Subscribe to log updates
  useEffect(() => {
    return devLogStore.subscribe(setLogs);
  }, []);

  // Filter logs
  const filteredLogs = filter === 'all' 
    ? logs 
    : logs.filter(log => log.type === filter);

  const handleClear = useCallback(() => {
    devLogStore.clearLogs();
  }, []);

  const handleToggleEnabled = useCallback(() => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    devLogStore.setEnabled(newEnabled);
  }, [enabled]);

  // Always render - this is a dev-only environment
  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed right-0 top-1/2 -translate-y-1/2 z-50',
          'p-2 bg-slate-800 text-white rounded-l-lg shadow-lg',
          'hover:bg-slate-700 transition-colors',
          'flex flex-col items-center gap-1',
          className
        )}
        title="Developer Panel"
      >
        <CodeIcon className="w-5 h-5" />
        {logs.some(l => l.status === 'error') && (
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        )}
        {logs.some(l => l.status === 'pending') && (
          <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
        )}
      </button>

      {/* Panel */}
      <div
        ref={panelRef}
        className={cn(
          'fixed right-0 top-0 bottom-0 z-40 w-96 bg-slate-900 shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <CodeIcon className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Dev Panel</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleEnabled}
              className={cn(
                'px-2 py-1 rounded text-xs font-medium transition-colors',
                enabled 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-slate-700 text-slate-400'
              )}
            >
              {enabled ? 'ON' : 'OFF'}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 text-slate-400 hover:text-white transition-colors"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 p-3 border-b border-slate-700">
          <FilterButton
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            count={logs.length}
          >
            All
          </FilterButton>
          <FilterButton
            active={filter === 'api'}
            onClick={() => setFilter('api')}
            count={logs.filter(l => l.type === 'api').length}
            color="blue"
          >
            API
          </FilterButton>
          <FilterButton
            active={filter === 'defender'}
            onClick={() => setFilter('defender')}
            count={logs.filter(l => l.type === 'defender').length}
            color="purple"
          >
            Defender
          </FilterButton>
          <FilterButton
            active={filter === 'error'}
            onClick={() => setFilter('error')}
            count={logs.filter(l => l.status === 'error').length}
            color="red"
          >
            Errors
          </FilterButton>
          <div className="flex-1" />
          <button
            onClick={handleClear}
            className="px-2 py-1 text-xs text-slate-400 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Logs list */}
        <div className="flex-1 overflow-y-auto">
          {filteredLogs.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CodeIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No logs yet</p>
              <p className="text-sm mt-1">API calls will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {filteredLogs.map(log => (
                <LogEntryCard key={log.id} log={log} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700 text-xs text-slate-500">
          <span>Last {logs.length} requests</span>
          <span className="float-right">Development mode</span>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black/20"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

// ============================================================================
// LOG ENTRY CARD
// ============================================================================

interface LogEntryCardProps {
  log: LogEntry;
}

function LogEntryCard({ log }: LogEntryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusColors: Record<LogStatus, string> = {
    pending: 'bg-yellow-500',
    success: 'bg-green-500',
    error: 'bg-red-500',
  };

  const typeColors: Record<LogType, string> = {
    api: 'text-blue-400',
    defender: 'text-purple-400',
    error: 'text-red-400',
  };

  const methodColors: Record<string, string> = {
    GET: 'text-green-400',
    POST: 'text-blue-400',
    PATCH: 'text-amber-400',
    DELETE: 'text-red-400',
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div 
      className={cn(
        'p-3 hover:bg-slate-800/50 transition-colors cursor-pointer',
        log.status === 'error' && 'bg-red-900/10'
      )}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {/* Header row */}
      <div className="flex items-center gap-2">
        <span className={cn('w-2 h-2 rounded-full', statusColors[log.status])} />
        <span className={cn('text-xs font-mono', methodColors[log.method])}>
          {log.method}
        </span>
        <span className={cn('text-xs', typeColors[log.type])}>
          [{log.type.toUpperCase()}]
        </span>
        <span className="text-xs text-slate-400 font-mono truncate flex-1">
          {log.endpoint}
        </span>
        {log.duration !== undefined && (
          <span className="text-xs text-slate-500 font-mono">
            {log.duration}ms
          </span>
        )}
      </div>

      {/* Timestamp */}
      <div className="text-xs text-slate-500 mt-1 font-mono">
        {formatTime(log.timestamp)}
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="mt-3 space-y-2">
          {/* Payload */}
          {log.payload && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">Request</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(JSON.stringify(log.payload, null, 2));
                  }}
                  className="text-xs text-slate-500 hover:text-white"
                >
                  Copy
                </button>
              </div>
              <pre className="text-xs font-mono bg-slate-950 p-2 rounded overflow-x-auto text-slate-300 max-h-32">
                {String(JSON.stringify(log.payload, null, 2))}
              </pre>
            </div>
          )}

          {/* Response */}
          {log.response && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">Response</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(JSON.stringify(log.response, null, 2));
                  }}
                  className="text-xs text-slate-500 hover:text-white"
                >
                  Copy
                </button>
              </div>
              <pre className="text-xs font-mono bg-slate-950 p-2 rounded overflow-x-auto text-slate-300 max-h-32">
                {String(JSON.stringify(log.response, null, 2))}
              </pre>
            </div>
          )}

          {/* Error */}
          {log.error && (
            <div>
              <span className="text-xs text-red-400">Error</span>
              <pre className="text-xs font-mono bg-red-950 p-2 rounded overflow-x-auto text-red-300 mt-1">
                {log.error}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FILTER BUTTON
// ============================================================================

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  count: number;
  color?: 'blue' | 'purple' | 'red';
  children: string;
}

function FilterButton({ active, onClick, count, color, children }: FilterButtonProps) {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    red: 'bg-red-500/20 text-red-400 border-red-500/50',
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2 py-1 rounded text-xs font-medium transition-colors',
        active
          ? color 
            ? colorClasses[color]
            : 'bg-slate-700 text-white'
          : 'text-slate-400 hover:text-white'
      )}
    >
      {children}
      {count > 0 && (
        <span className="ml-1 opacity-70">({count})</span>
      )}
    </button>
  );
}

// ============================================================================
// ICONS
// ============================================================================

function CodeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
